import getActiveComp from 'common/src/modules/creativeTemplates/copilot/agents/adBuilderAgent/utils/getActiveComp';
import {TemplateAssetType} from 'common/src/modules/dynamicParameters/dynamicParameters.enums';
import {TemplateAsset} from 'common/src/modules/dynamicParameters/dynamicParameters.types';
import {MovieLayerType} from 'common/src/modules/movie/movie.enums';
import {MovieShapeLayer} from 'common/src/modules/movie/movieLayers.types';
import {Timestamped} from 'common/src/modules/movie/movieRendering';
import {IShape} from 'common/src/modules/movie/movieShapes.types';
import applyKeyframe from 'common/src/modules/movie/utils/applyKeyframe';
import {getLayerBoundingBoxRelativeToComposition} from 'common/src/modules/movie/utils/getLayerBoundingBoxRelativeToComposition';
import {
  CompWithWorkingProps,
  LayerWithWorkingProps,
} from 'common/src/modules/movieEditor/movieEditor.types';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {convertTextAnimatorsToSchema} from '../../utils/convertTextAnimatorsToSchema';
import {extractShapeToSchema} from '../../utils/extractShapeToSchema';
import {AdBuilderAgentToolName} from '../adBuilderAgent.enums';
import {
  LayerBoundingBoxData,
  LayerContentData,
  LayerData,
  LayerDataSection,
  LayerDetailsResult,
  LayerGeneralData,
  LayerTextAnimatorData,
  LayerTransformData,
} from '../adBuilderAgent.types';

function getLayerGeneralData(
  layer: LayerWithWorkingProps,
  activeComp: CompWithWorkingProps,
  timeParam?: number,
): LayerGeneralData {
  return {
    type: layer.ty,
    name: layer.name,
    startTime: Number((layer.s / 1000).toFixed(2)),
    endTime: Number((layer.e / 1000).toFixed(2)),
    time: timeParam !== undefined ? Number(timeParam.toFixed(2)) : 0,
    isHidden: layer.isHidden,
    isLocked: layer.isLocked,
    parentLayerId:
      layer.pi !== undefined
        ? activeComp.l.find((l: LayerWithWorkingProps) => l.i === layer.pi?.toString())?.id
        : undefined,
    zOrder: Number(layer.i),
  };
}

function getLayerTransformData(timedLayer: Timestamped<LayerWithWorkingProps>): LayerTransformData {
  return {
    transform: {
      position: {
        x: Number((timedLayer.tr.p as number[])[0].toFixed(2)),
        y: Number((timedLayer.tr.p as number[])[1].toFixed(2)),
        z: Number((timedLayer.tr.p as number[])[2].toFixed(2)),
      },
      anchor: {
        x: Number((timedLayer.tr.an as number[])[0].toFixed(2)),
        y: Number((timedLayer.tr.an as number[])[1].toFixed(2)),
        z: Number((timedLayer.tr.an as number[])[2].toFixed(2)),
      },
      scale: {
        x: Number((timedLayer.tr.s as number[])[0].toFixed(3)),
        y: Number((timedLayer.tr.s as number[])[1].toFixed(3)),
        z: Number((timedLayer.tr.s as number[])[2].toFixed(3)),
      },
      rotation: {
        x: timedLayer.tr.rx
          ? Number((((timedLayer.tr.rx as number) * 180) / Math.PI).toFixed(2))
          : undefined,
        y: timedLayer.tr.ry
          ? Number((((timedLayer.tr.ry as number) * 180) / Math.PI).toFixed(2))
          : undefined,
        z: Number((((timedLayer.tr.rz as number) * 180) / Math.PI).toFixed(2)),
      },
      opacity: timedLayer.tr.a as number,
    },
  };
}

function getLayerBoundingBoxData(
  layer: LayerWithWorkingProps,
  activeComp: CompWithWorkingProps,
  timeInMs: number,
): LayerBoundingBoxData {
  const boundingBox = getLayerBoundingBoxRelativeToComposition(
    layer,
    activeComp,
    {[activeComp.id]: activeComp},
    timeInMs,
  );

  return {
    boundingBoxRelativeToComposition: boundingBox || undefined,
  };
}

function getLayerContentData(
  timedLayer: Timestamped<LayerWithWorkingProps>,
  timeInMs: number,
  store: CLStore,
): LayerContentData {
  const contentData: LayerContentData = {};
  const state = store.getState();

  switch (timedLayer.ty) {
    case MovieLayerType.Text:
      contentData.text = {
        lines: timedLayer.text.aeSrcTxt.txtObj?.lines || [],
        boxSize: timedLayer.text.aeSrcTxt.boxTxtS,
        boxShiftRelativeToLayer: timedLayer.text.aeSrcTxt.boxTxtP,
      };
      break;

    case MovieLayerType.Image:
      const templateAssets = state.creativeTemplatePage.movieAssets.templateAssets;
      const matchingAsset = templateAssets.find(
        (asset: TemplateAsset) =>
          asset.metadata.type === TemplateAssetType.Image &&
          asset.metadata.url === timedLayer.image.url,
      );

      contentData.image = {
        assetId: matchingAsset?.id || null,
        description: matchingAsset?.metadata.customMetadata?.ContentDescription || null,
        width: timedLayer.image.w,
        height: timedLayer.image.h,
        shiftRelativeToLayer: {
          x: timedLayer.image.x || 0,
          y: timedLayer.image.y || 0,
        },
      };
      break;

    case MovieLayerType.Shape:
      const shape = (timedLayer as Timestamped<MovieShapeLayer>).shape.shapes[0];

      if (shape) {
        contentData.shape = extractShapeToSchema(shape as unknown as IShape, timeInMs);
      }

      break;
  }

  return contentData;
}

function getLayerTextAnimatorData(layer: LayerWithWorkingProps): LayerTextAnimatorData {
  const textAnimatorData: LayerTextAnimatorData = {};

  if (layer.ty === MovieLayerType.Text && layer.text && layer.text.animators) {
    textAnimatorData.animators = convertTextAnimatorsToSchema(layer.text.animators);
  }

  return textAnimatorData;
}

function filterLayerDataBySection(
  layerData: LayerData,
  requestSection: LayerDataSection,
): Partial<LayerData> {
  if (requestSection === LayerDataSection.All) {
    return layerData;
  }

  const filteredData: Partial<LayerData> = {};

  switch (requestSection) {
    case LayerDataSection.General:
      filteredData.type = layerData.type;
      filteredData.name = layerData.name;
      filteredData.startTime = layerData.startTime;
      filteredData.endTime = layerData.endTime;
      filteredData.time = layerData.time;
      filteredData.isHidden = layerData.isHidden;
      filteredData.isLocked = layerData.isLocked;
      filteredData.parentLayerId = layerData.parentLayerId;
      filteredData.zOrder = layerData.zOrder;
      break;
    case LayerDataSection.Transform:
      filteredData.transform = layerData.transform;
      break;
    case LayerDataSection.BoundingBox:
      filteredData.boundingBoxRelativeToComposition = layerData.boundingBoxRelativeToComposition;
      break;
    case LayerDataSection.Content:
      if (layerData.text) filteredData.text = layerData.text;
      if (layerData.image) filteredData.image = layerData.image;
      if (layerData.shape) filteredData.shape = layerData.shape;
      break;
    case LayerDataSection.TextAnimators:
      if (layerData.animators) filteredData.animators = layerData.animators;
      break;
  }

  return filteredData;
}

const inputSchema = z.object({
  layerIds: z.array(z.string()).describe('Array of layer IDs to get details for'),
  requestSection: z
    .nativeEnum(LayerDataSection)
    .default(LayerDataSection.General)
    .describe('Section of data to retrieve'),
  time: z.number().optional().describe('Time in seconds to get layer details at (defaults to 0)'),
});

function getLayerDetailsTool(store: CLStore) {
  return tool({
    name: AdBuilderAgentToolName.GetLayerDetails,
    displayName: 'Getting layer details',
    description:
      'Retrieves comprehensive information about single or multiple layers in the active composition.',
    inputSchema,
    execute: async ({input: params}: ToolExecuteParams<typeof inputSchema>) => {
      const activeComp = getActiveComp(store);

      if (!activeComp) {
        return {
          content: 'ERROR: No active composition found',
        };
      }

      try {
        const timeInMs = params.time !== undefined ? params.time * 1000 : 0;

        // Handle array of layer IDs
        const layerIds = params.layerIds;
        const results: LayerDetailsResult[] = [];

        for (const layerId of layerIds) {
          const layer = activeComp.l.find((l: LayerWithWorkingProps) => l.id === layerId);

          if (!layer) {
            results.push({
              layerId,
              error: `Layer with ID "${layerId}" not found`,
            });
            continue;
          }

          // Get layer at specific time
          const timedLayer = applyKeyframe(layer, timeInMs);

          // Build complete layer data
          const generalData = getLayerGeneralData(layer, activeComp, params.time);
          const transformData = getLayerTransformData(timedLayer);
          const boundingBoxData =
            params.requestSection === LayerDataSection.BoundingBox ||
            params.requestSection === LayerDataSection.All
              ? getLayerBoundingBoxData(layer, activeComp, timeInMs)
              : {};
          const contentData =
            params.requestSection === LayerDataSection.Content ||
            params.requestSection === LayerDataSection.All
              ? getLayerContentData(timedLayer, timeInMs, store)
              : {};
          const textAnimatorData =
            params.requestSection === LayerDataSection.TextAnimators ||
            params.requestSection === LayerDataSection.All
              ? getLayerTextAnimatorData(layer)
              : {};

          const completeLayerData: LayerData = {
            ...generalData,
            ...transformData,
            ...boundingBoxData,
            ...contentData,
            ...textAnimatorData,
          };

          // Filter data based on requested section
          const filteredData = filterLayerDataBySection(completeLayerData, params.requestSection);

          results.push({
            layerId,
            data: filteredData,
          });
        }

        return {
          content: JSON.stringify(results),
        };
      } catch (error) {
        return {
          content: `ERROR: ${error instanceof Error ? error.message : 'Failed to get layer details'}`,
        };
      }
    },
  });
}

export default getLayerDetailsTool;
